/**
 * TaskBoard Component
 *
 * Kanban board with drag-and-drop support for task management.
 * Uses @dnd-kit for drag-and-drop and @tanstack/react-virtual for performance.
 *
 * @module components/tasks/TaskBoard
 */

import { memo, useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  MeasuringStrategy,
  pointerWithin,
  rectIntersection
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { LayoutGrid, List, Filter, Search, ExternalLink, X, ChevronDown, User, Tag, Plus, Wifi, WifiOff, RefreshCcw, FolderKanban, Archive, ChevronRight } from 'lucide-react';
import { TaskColumnSimple } from './TaskColumn.jsx';
import StackedColumn from './StackedColumn.jsx';
import TaskCard from './TaskCard.jsx';
import { TASK_STATUS } from '../../contexts/TaskContext.jsx';
import { Avatar } from '../common/Avatar.jsx';

// ============================================
// Constants
// ============================================

/**
 * Column layout configuration for compact Kanban board
 * 5 visual columns for active workflow (responsive flex-grow)
 * Backlog + Blocked are in a separate collapsible archive sidebar
 *
 * Main Layout:
 * 1. Todo (single)
 * 2. Doing (single)
 * 3. Agent Test + User Test (stacked)
 * 4. Review (single)
 * 5. Done (single)
 */
const COLUMN_LAYOUT = [
  { type: 'single', status: TASK_STATUS.TODO },
  { type: 'single', status: TASK_STATUS.DOING },
  { type: 'stacked', topStatus: TASK_STATUS.AGENT_TEST, bottomStatus: TASK_STATUS.USER_TEST },
  { type: 'single', status: TASK_STATUS.REVIEW },
  { type: 'single', status: TASK_STATUS.DONE }
];


// ============================================
// Component
// ============================================

/**
 * TaskBoard Component
 *
 * @param {object} props - Component props
 * @param {object} props.tasksByStatus - Tasks grouped by status
 * @param {function} [props.onTaskClick] - Handler when a task is clicked
 * @param {function} [props.onAddTask] - Handler when add task button is clicked
 * @param {function} [props.onStatusChange] - Handler when task status changes via drag
 * @param {function} [props.onCreateTask] - Handler when create task button is clicked
 * @param {Set} [props.highlightedTaskIds] - Set of task IDs to highlight (for live updates)
 * @param {boolean} [props.isPopout=false] - Whether this is rendered in a popout window
 * @param {boolean} [props.showPopoutButton=true] - Whether to show the popout button
 * @param {object} [props.wsStatus] - WebSocket status { isConnected, isSubscribed, state }
 * @param {array} [props.projects] - Available projects for selector
 * @param {object} [props.selectedProject] - Currently selected project
 * @param {function} [props.onProjectChange] - Handler when project is changed
 * @param {string} [props.className=''] - Additional CSS classes
 * @returns {React.ReactElement} Board element
 */
const TaskBoard = memo(function TaskBoard({
  tasksByStatus = {},
  onTaskClick,
  onAddTask,
  onStatusChange,
  onCreateTask,
  highlightedTaskIds = new Set(),
  isPopout = false,
  showPopoutButton = true,
  wsStatus = null,
  projects = [],
  selectedProject = null,
  onProjectChange,
  className = ''
}) {
  const [activeTask, setActiveTask] = useState(null);
  const [viewMode, setViewMode] = useState('board'); // 'board' or 'list'
  const [showArchive, setShowArchive] = useState(false); // Collapsible Backlog/Blocked sidebar

  // Filter and search state
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [selectedLabels, setSelectedLabels] = useState([]);
  const filterDropdownRef = useRef(null);
  const projectDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setShowFilterDropdown(false);
      }
      if (projectDropdownRef.current && !projectDropdownRef.current.contains(event.target)) {
        setShowProjectDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Extract unique assignees and labels from all tasks
  const { uniqueAssignees, uniqueLabels } = useMemo(() => {
    const assigneesMap = new Map();
    const labelsSet = new Set();

    Object.values(tasksByStatus).flat().forEach(task => {
      if (task.assignee) {
        assigneesMap.set(task.assignee.id, task.assignee);
      }
      if (task.labels && Array.isArray(task.labels)) {
        task.labels.forEach(label => labelsSet.add(label));
      }
    });

    return {
      uniqueAssignees: Array.from(assigneesMap.values()),
      uniqueLabels: Array.from(labelsSet)
    };
  }, [tasksByStatus]);

  // Filter tasks based on search query and filters
  const filteredTasksByStatus = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    const hasFilters = query || selectedAssignees.length > 0 || selectedLabels.length > 0;

    if (!hasFilters) {
      return tasksByStatus;
    }

    const filtered = {};

    Object.entries(tasksByStatus).forEach(([status, tasks]) => {
      filtered[status] = tasks.filter(task => {
        // Search filter - matches title or description
        if (query) {
          const titleMatch = task.title?.toLowerCase().includes(query);
          const descMatch = task.description?.toLowerCase().includes(query);
          if (!titleMatch && !descMatch) return false;
        }

        // Assignee filter
        if (selectedAssignees.length > 0) {
          if (!task.assignee || !selectedAssignees.includes(task.assignee.id)) {
            return false;
          }
        }

        // Labels filter
        if (selectedLabels.length > 0) {
          if (!task.labels || !selectedLabels.some(label => task.labels.includes(label))) {
            return false;
          }
        }

        return true;
      });
    });

    return filtered;
  }, [tasksByStatus, searchQuery, selectedAssignees, selectedLabels]);

  // Check if any filters are active
  const hasActiveFilters = selectedAssignees.length > 0 || selectedLabels.length > 0;

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSelectedAssignees([]);
    setSelectedLabels([]);
    setSearchQuery('');
  }, []);

  // Toggle assignee filter
  const toggleAssignee = useCallback((assigneeId) => {
    setSelectedAssignees(prev =>
      prev.includes(assigneeId)
        ? prev.filter(id => id !== assigneeId)
        : [...prev, assigneeId]
    );
  }, []);

  // Toggle label filter
  const toggleLabel = useCallback((label) => {
    setSelectedLabels(prev =>
      prev.includes(label)
        ? prev.filter(l => l !== label)
        : [...prev, label]
    );
  }, []);

  // Handle popout button click
  const handlePopout = useCallback(() => {
    const popoutUrl = `${window.location.origin}/tasks?popout=true`;
    window.open(
      popoutUrl,
      'task-board-popout',
      'width=1200,height=800,menubar=no,toolbar=no,location=no,status=no'
    );
  }, []);

  // Configure DnD sensors with touch support
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8 // Minimum drag distance before activation
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  // Custom collision detection for better Kanban behavior
  // Uses pointer position first, falls back to rectangle intersection
  const collisionDetection = useCallback((args) => {
    // First try pointer-based collision (more accurate during drag)
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) {
      return pointerCollisions;
    }
    // Fallback to rectangle intersection
    return rectIntersection(args);
  }, []);

  // Find task by ID across all columns
  const findTaskById = useCallback((taskId) => {
    for (const [status, tasks] of Object.entries(tasksByStatus)) {
      const task = tasks.find(t => t.id === taskId);
      if (task) {
        return { task, status };
      }
    }
    return null;
  }, [tasksByStatus]);

  // DnD handlers
  const handleDragStart = useCallback((event) => {
    const { active } = event;
    const result = findTaskById(active.id);
    if (result) {
      setActiveTask(result.task);
    }
  }, [findTaskById]);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    setActiveTask(null);

    if (!over) return;

    const taskId = active.id;
    const newStatus = over.id;

    // Find the task's current status
    const result = findTaskById(taskId);
    if (!result) return;

    const { status: oldStatus } = result;

    // Only trigger if status actually changed
    if (oldStatus !== newStatus && onStatusChange) {
      onStatusChange(taskId, newStatus, oldStatus);
    }
  }, [findTaskById, onStatusChange]);

  const handleDragCancel = useCallback(() => {
    setActiveTask(null);
  }, []);

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Board Header / Toolbar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold text-gray-200">Task Management</h2>
          <div className="flex items-center gap-1 bg-gray-800 rounded-lg p-1">
            <button
              onClick={() => setViewMode('board')}
              className={`p-1.5 rounded ${
                viewMode === 'board'
                  ? 'bg-gray-700 text-gray-200'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
              title="Board view"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded ${
                viewMode === 'list'
                  ? 'bg-gray-700 text-gray-200'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
              title="List view"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
          {/* Popout Button */}
          {showPopoutButton && !isPopout && (
            <button
              onClick={handlePopout}
              className="p-1.5 text-gray-400 hover:text-gray-200 hover:bg-gray-700 rounded-lg transition-colors"
              title="Open in new window"
            >
              <ExternalLink className="w-4 h-4" />
            </button>
          )}
          {/* Archive Toggle Button */}
          <button
            onClick={() => setShowArchive(!showArchive)}
            className={`flex items-center gap-1.5 px-2 py-1.5 text-sm rounded-lg transition-colors ${
              showArchive
                ? 'bg-gray-700 text-gray-200'
                : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
            }`}
            title={showArchive ? 'Hide Archive' : 'Show Archive'}
          >
            <Archive className="w-4 h-4" />
            <span className="hidden sm:inline">{showArchive ? 'Hide' : 'Show'} Archive</span>
            <ChevronRight className={`w-3 h-3 transition-transform ${showArchive ? 'rotate-180' : ''}`} />
          </button>
        </div>
        <div className="flex items-center gap-2">
          {/* Project Selector */}
          {projects.length > 0 && onProjectChange && (
            <div className="relative" ref={projectDropdownRef}>
              <button
                onClick={() => setShowProjectDropdown(!showProjectDropdown)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <FolderKanban className="w-4 h-4 text-indigo-400" />
                <span className="text-gray-200 max-w-32 truncate">
                  {selectedProject?.displayName || selectedProject?.name || 'Select Project'}
                </span>
                <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${showProjectDropdown ? 'rotate-180' : ''}`} />
              </button>

              {/* Project Dropdown Menu */}
              {showProjectDropdown && (
                <div className="absolute left-0 top-full mt-1 w-56 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto">
                  {projects.map((project) => (
                    <button
                      key={project.id}
                      onClick={() => {
                        onProjectChange(project);
                        setShowProjectDropdown(false);
                      }}
                      className={`
                        w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors
                        ${selectedProject?.id === project.id
                          ? 'bg-indigo-600 text-white'
                          : 'text-gray-300 hover:bg-gray-700'
                        }
                      `}
                    >
                      <FolderKanban className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{project.displayName || project.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* WebSocket Status Indicator */}
          {wsStatus && (
            <div
              className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs ${
                wsStatus.isConnected && wsStatus.isSubscribed
                  ? 'bg-emerald-500/20 text-emerald-400'
                  : wsStatus.state === 'reconnecting'
                  ? 'bg-amber-500/20 text-amber-400'
                  : 'bg-red-500/20 text-red-400'
              }`}
              title={
                wsStatus.isConnected && wsStatus.isSubscribed
                  ? 'Real-time updates active'
                  : wsStatus.state === 'reconnecting'
                  ? 'Reconnecting...'
                  : 'Disconnected - updates may be delayed'
              }
            >
              {wsStatus.isConnected && wsStatus.isSubscribed ? (
                <Wifi className="w-3.5 h-3.5" />
              ) : wsStatus.state === 'reconnecting' ? (
                <RefreshCcw className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <WifiOff className="w-3.5 h-3.5" />
              )}
              <span className="hidden sm:inline">
                {wsStatus.isConnected && wsStatus.isSubscribed ? 'Live' : wsStatus.state === 'reconnecting' ? 'Reconnecting' : 'Offline'}
              </span>
            </div>
          )}

          {/* New Task Button */}
          {onCreateTask && (
            <button
              onClick={onCreateTask}
              className={`flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors ${isPopout ? 'px-3 py-1.5 text-sm' : 'px-4 py-2'}`}
            >
              <Plus className="w-4 h-4" />
              New Task
            </button>
          )}
          {/* Filter Dropdown */}
          <div className="relative" ref={filterDropdownRef}>
            <button
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
              className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                hasActiveFilters
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
              }`}
            >
              <Filter className="w-4 h-4" />
              <span>Filter</span>
              {hasActiveFilters && (
                <span className="bg-white/20 px-1.5 py-0.5 rounded text-xs">
                  {selectedAssignees.length + selectedLabels.length}
                </span>
              )}
              <ChevronDown className="w-3 h-3" />
            </button>

            {/* Filter Dropdown Menu */}
            {showFilterDropdown && (
              <div className="absolute right-0 top-full mt-1 w-64 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50">
                {/* Assignee Filter Section */}
                {uniqueAssignees.length > 0 && (
                  <div className="p-3 border-b border-gray-700">
                    <div className="flex items-center gap-2 text-xs font-medium text-gray-400 mb-2">
                      <User className="w-3 h-3" />
                      <span>Assignee</span>
                    </div>
                    <div className="space-y-1">
                      {uniqueAssignees.map(assignee => (
                        <button
                          key={assignee.id}
                          onClick={() => toggleAssignee(assignee.id)}
                          className={`w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded transition-colors ${
                            selectedAssignees.includes(assignee.id)
                              ? 'bg-indigo-600 text-white'
                              : 'text-gray-300 hover:bg-gray-700'
                          }`}
                        >
                          <Avatar
                            name={assignee.displayName || assignee.name}
                            avatarUrl={assignee.avatarUrl}
                            type={assignee.type}
                            size="xs"
                          />
                          <span className="truncate">{assignee.displayName || assignee.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Labels Filter Section */}
                {uniqueLabels.length > 0 && (
                  <div className="p-3 border-b border-gray-700">
                    <div className="flex items-center gap-2 text-xs font-medium text-gray-400 mb-2">
                      <Tag className="w-3 h-3" />
                      <span>Labels</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {uniqueLabels.map(label => (
                        <button
                          key={label}
                          onClick={() => toggleLabel(label)}
                          className={`px-2 py-1 text-xs rounded transition-colors ${
                            selectedLabels.includes(label)
                              ? 'bg-indigo-600 text-white'
                              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* No filters available */}
                {uniqueAssignees.length === 0 && uniqueLabels.length === 0 && (
                  <div className="p-3 text-sm text-gray-500 text-center">
                    No filters available
                  </div>
                )}

                {/* Clear Filters Button */}
                {hasActiveFilters && (
                  <div className="p-2">
                    <button
                      onClick={clearFilters}
                      className="w-full flex items-center justify-center gap-2 px-3 py-1.5 text-sm text-gray-400 hover:text-gray-200 hover:bg-gray-700 rounded transition-colors"
                    >
                      <X className="w-3 h-3" />
                      <span>Clear all filters</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search tasks..."
              className="pl-9 pr-8 py-1.5 text-sm bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-500 focus:outline-none focus:border-indigo-500 w-48"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

        </div>
      </div>

      {/* Board Content */}
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
        measuring={{
          droppable: {
            strategy: MeasuringStrategy.Always
          }
        }}
      >
        <div className="flex-1 flex min-h-0 overflow-hidden">
          {/* Main Workflow Columns */}
          <div
            className="
              flex-1 flex gap-3 p-4 min-h-0
              [&::-webkit-scrollbar]:h-2
              [&::-webkit-scrollbar-track]:bg-gray-800
              [&::-webkit-scrollbar-thumb]:bg-gray-600
              [&::-webkit-scrollbar-thumb]:rounded-full
            "
          >
            {COLUMN_LAYOUT.map((column) => (
              <div
                key={column.type === 'single' ? column.status : `${column.topStatus}-${column.bottomStatus}`}
                className="min-w-[160px] flex-1 basis-0 h-full"
              >
                {column.type === 'single' ? (
                  <TaskColumnSimple
                    status={column.status}
                    tasks={filteredTasksByStatus[column.status] || []}
                    onTaskClick={onTaskClick}
                    onAddTask={onAddTask}
                    highlightedTaskIds={highlightedTaskIds}
                    className="h-full"
                  />
                ) : (
                  <StackedColumn
                    topStatus={column.topStatus}
                    bottomStatus={column.bottomStatus}
                    tasksByStatus={filteredTasksByStatus}
                    onTaskClick={onTaskClick}
                    onAddTask={onAddTask}
                    highlightedTaskIds={highlightedTaskIds}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Archive Sidebar (Backlog + Blocked) */}
          {showArchive && (
            <div className="flex-shrink-0 w-64 border-l border-gray-700 bg-gray-900/50 p-3 h-full">
              <StackedColumn
                topStatus={TASK_STATUS.BACKLOG}
                bottomStatus={TASK_STATUS.BLOCKED}
                tasksByStatus={filteredTasksByStatus}
                onTaskClick={onTaskClick}
                onAddTask={onAddTask}
                highlightedTaskIds={highlightedTaskIds}
                className="h-full"
              />
            </div>
          )}
        </div>

        {/* Drag Overlay - the card shown while dragging */}
        {/* dropAnimation is null for Kanban boards - the optimistic update
            handles the visual transition instantly when dropped */}
        <DragOverlay dropAnimation={null}>
          {activeTask ? (
            <div className="w-72">
              <TaskCard
                task={activeTask}
                isOverlay
              />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
});

export default TaskBoard;
